import type { DataNode, TreeProps } from 'antd/es/tree'
import {
  BarChartOutlined,
  BookOutlined,
  FolderOutlined,
} from '@ant-design/icons'

import type { DatalensTreeNode } from '@/api/datalens'

export interface TreeNodeData extends DataNode {
  nodeType: 'collection' | 'workbook' | 'dash'
  children?: TreeNodeData[]
  orgIds: string[]
  orgScopeSources: string[]
  orgScoped: boolean
}

export const nodeIcon: TreeProps['icon'] = (props) => {
  const nodeType = (props as { data?: TreeNodeData })?.data?.nodeType
  switch (nodeType) {
    case 'collection':
      return <FolderOutlined style={{ color: '#1677ff' }} />
    case 'workbook':
      return <BookOutlined style={{ color: '#faad14' }} />
    case 'dash':
      return <BarChartOutlined style={{ color: '#52c41a' }} />
    default:
      return <FolderOutlined />
  }
}

export function convertTreeNodes(nodes: DatalensTreeNode[]): TreeNodeData[] {
  return nodes.map((n) => ({
    key: n.key,
    title: n.title,
    nodeType: n.nodeType,
    isLeaf: n.isLeaf,
    orgIds: n.orgIds || [],
    orgScopeSources: n.orgScopeSources || [],
    orgScoped: Boolean(n.orgScoped),
    children: n.children.length > 0 ? convertTreeNodes(n.children) : undefined,
  }))
}

export function filterTree(nodes: TreeNodeData[], search: string): TreeNodeData[] {
  if (!search) return nodes
  const lower = search.toLowerCase()

  function filter(list: TreeNodeData[]): TreeNodeData[] {
    const result: TreeNodeData[] = []
    for (const n of list) {
      const title = typeof n.title === 'string' ? n.title : ''
      if (title.toLowerCase().includes(lower)) {
        result.push(n)
      } else if (n.children) {
        const filteredChildren = filter(n.children)
        if (filteredChildren.length > 0) {
          result.push({ ...n, children: filteredChildren })
        }
      }
    }
    return result
  }

  return filter(nodes)
}

export function collectAllKeys(nodes: TreeNodeData[]): string[] {
  const keys: string[] = []
  function walk(list: TreeNodeData[]) {
    for (const n of list) {
      if (n.children && n.children.length > 0) {
        keys.push(n.key as string)
        walk(n.children)
      }
    }
  }
  walk(nodes)
  return keys
}

export function findTitleByKey(nodes: TreeNodeData[], key: string): string | null {
  for (const n of nodes) {
    if (n.key === key) {
      return typeof n.title === 'string' ? n.title : null
    }
    if (n.children) {
      const found = findTitleByKey(n.children, key)
      if (found) return found
    }
  }
  return null
}

export function entryIdFromKey(key: string): string {
  return key.startsWith('entry_') ? key.slice(6) : key
}

export function keyFromEntryId(entryId: string): string {
  return `entry_${entryId}`
}

export function isEntryKey(key: string): boolean {
  return key.startsWith('entry_')
}
